import Dexie, { Table } from 'dexie';
import type { AdvancedDashboardData } from '../features/dashboard/dashboardAdvancedTypes';
import type { DashboardResponse } from '../features/dashboard/api';

export type DashboardCacheMode = 'simple' | 'advanced';

/**
 * Cache key for a list, scoped to the signed-in user: rows come back filtered by
 * that user's data scope, so they must not be reused for anyone else.
 * Returns null while the user is unknown, which disables caching.
 */
export const cachedListKey = (baseKey?: string, userId?: number | string | null): string | null => {
  if (!baseKey) return null;
  const id = Number(userId);
  if (!Number.isFinite(id) || id < 1) return null;
  return `${baseKey}:${id}`;
};

/** Base name of a scoped list key, for labels and cleanup. */
export const cachedListBaseKey = (key: string): string => key.replace(/:\d+$/, "");

let purged = false;

/**
 * Drop cache entries written before lists were scoped per user. They were
 * shared between whoever used the browser, so they are deleted rather than
 * handed to the current user. Runs once per page load.
 */
export const purgeUnscopedCaches = async (): Promise<void> => {
  if (purged) return;
  purged = true;
  try {
    const listKeys = await localCacheDB.records.toCollection().primaryKeys();
    const staleLists = (listKeys as string[]).filter((key) => !/:\d+$/.test(key));
    if (staleLists.length) await localCacheDB.records.bulkDelete(staleLists);

    const dashboardKeys = await localCacheDB.dashboardEntries.toCollection().primaryKeys();
    const staleDashboards = (dashboardKeys as string[]).filter((key) => !/:u\d+$/.test(key));
    if (staleDashboards.length) await localCacheDB.dashboardEntries.bulkDelete(staleDashboards);
  } catch (error) {
    console.error('Failed to purge unscoped caches:', error);
  }
};

export interface CachedData<T = any> {
  key: string;
  data: T[];
  lastUpdate: string;
  period?: { from: string | null; to: string | null };
}

export interface DashboardCachedPayload {
  base: DashboardResponse;
  advanced?: AdvancedDashboardData;
}

export interface DashboardCacheRecord {
  cacheKey: string;
  from: string;
  to: string;
  mode: DashboardCacheMode;
  fingerprint: string;
  fetchedAt: string;
  payload: DashboardCachedPayload;
}

/** Cached product image (blob + content hash) for POS catalog / thumbs. */
export interface ItemImageCacheRecord {
  /** `${itemId}:${role}` */
  cacheKey: string;
  itemId: number;
  role: string;
  /** SHA-256 hex of the image bytes */
  hash: string;
  blob: Blob;
  mimeType: string;
  updatedAt: string;
  /** Remote URL used when the image was last downloaded */
  sourceUrl?: string;
}

class ShopendCacheDB extends Dexie {
  records!: Table<CachedData, string>;
  dashboardEntries!: Table<DashboardCacheRecord, string>;
  itemImages!: Table<ItemImageCacheRecord, string>;

  constructor() {
    super('ShopendCacheDB');
    this.version(1).stores({
      records: 'key',
    });
    this.version(2).stores({
      records: 'key',
      dashboardEntries: 'cacheKey',
    });
    // Re-declare stores so upgrades from v1 reliably create dashboardEntries.
    this.version(3).stores({
      records: 'key',
      dashboardEntries: 'cacheKey, from, to, mode, fetchedAt',
    });
    this.version(4).stores({
      records: 'key',
      dashboardEntries: 'cacheKey, from, to, mode, fetchedAt',
      itemImages: 'cacheKey, itemId, role, hash, updatedAt',
    });
  }
}

export const localCacheDB = new ShopendCacheDB();
